"use client";

import { Input } from "@workspace/ui/components/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@workspace/ui/components/tooltip";

function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
}

function formatAbsoluteDate(dateStr: string): string {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function toInputDate(dateStr: string): string {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
}

interface DateFieldProps {
  value?: string | null;
  editing?: boolean;
  onChange?: (date: string | null) => void;
}

export function DateField({ value, editing, onChange }: DateFieldProps) {
  if (!editing) {
    if (!value) {
      return <span className="text-sm text-muted-foreground italic">—</span>;
    }
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-sm text-foreground cursor-default">
              {formatRelativeDate(value)}
            </span>
          </TooltipTrigger>
          <TooltipContent side="left" className="text-sm">
            {formatAbsoluteDate(value)}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Input
      type="date"
      value={value ? toInputDate(value) : ""}
      onChange={(e) => {
        const val = e.target.value;
        onChange?.(val ? new Date(val).toISOString() : null);
      }}
      className="h-8 text-sm px-2"
    />
  );
}
